import { Button } from "@solomon-labs/design/components/ui";
import React from "react";

type TransitionStyle = "slide" | "fade-shrink-blur";
type InitialStatus = "success" | "error" | "pending";

interface TransitionButtonProps {
    /** Current phase — drives which label/status to show */
    showCta: boolean;
    /**
     * Label shown in the *initial* state (when `showCta` is false). Named
     * `successLabel` for backwards-compat with the success-flow callers; for
     * an error flow this is the "X Failed" copy.
     */
    successLabel: string;
    /** Label shown in CTA state (when `showCta` is true) */
    ctaLabel: string;
    /** onClick handler for the CTA state */
    onClick?: () => void;
    /**
     * Status applied to the underlying Button while `showCta` is false. Once
     * `showCta` flips true, status becomes undefined so the button looks
     * neutral / clickable. Default `"success"` for backwards compat.
     */
    initialStatus?: InitialStatus;
    /**
     * Visual transition between the two labels.
     * - `"slide"` (default): translateY crossfade — initial label slides up
     *   and out, CTA slides up from below.
     * - `"fade-shrink-blur"`: simultaneous crossfade with scale-down +
     *   gaussian blur on the outgoing label and the inverse on the incoming
     *   label. Used for error → recovery transitions where the failure has
     *   already registered visually and we want a softer pivot to the
     *   "return to X" affordance.
     */
    transition?: TransitionStyle;
}

/**
 * Button that transitions between an initial label (success or error) and a
 * follow-up CTA label. Two transition styles:
 *
 * - **slide** — translateY crossfade (used for `Staked! → Stake more`,
 *   `Cooldown Started! → Unstake more`, etc.)
 * - **fade-shrink-blur** — opacity + scale + blur crossfade (used for
 *   `Buy Failed → Return to Buy` and the rest of the error-recovery loop)
 *
 * @version 2.0.0
 * @history
 *   2.0.0 — Added `initialStatus` and `transition` props. The `slide`
 *           default is the original v1 behavior; `fade-shrink-blur` is the
 *           new error-recovery transition.
 *   1.0.0 — Slide-only crossfade for success-state CTAs.
 */
export function TransitionButton({
    showCta,
    successLabel,
    ctaLabel,
    onClick,
    initialStatus = "success",
    transition = "slide"
}: TransitionButtonProps): React.ReactElement {
    const isFade = transition === "fade-shrink-blur";

    const initialLabelStyle: React.CSSProperties = isFade
        ? {
              opacity: showCta ? 0 : 1,
              transform: showCta ? "scale(0.85)" : "scale(1)",
              filter: showCta ? "blur(4px)" : "blur(0)"
          }
        : {
              transform: showCta ? "translateY(-100%)" : "translateY(0)",
              opacity: showCta ? 0 : 1
          };

    const ctaLabelStyle: React.CSSProperties = isFade
        ? {
              opacity: showCta ? 1 : 0,
              transform: showCta ? "scale(1)" : "scale(0.85)",
              filter: showCta ? "blur(0)" : "blur(4px)"
          }
        : {
              transform: showCta ? "translateY(0)" : "translateY(100%)",
              opacity: showCta ? 1 : 0
          };

    // For the slide transition (success → next-CTA), status drops to
    // undefined when the CTA appears so the button reads as a fresh
    // clickable affordance. For fade-shrink-blur (error → return), the
    // status stays at `initialStatus` throughout — the user wants the
    // error coloring (red underline-bar etc.) to persist while the
    // button just becomes interactive (hoverable + clickable). Click
    // is still gated on `showCta` regardless so the user can't dismiss
    // before the transition window opens.
    const buttonStatus = isFade
        ? initialStatus
        : showCta
          ? undefined
          : initialStatus;

    return (
        <Button
            variant="raised"
            size="lg"
            interaction="light-bar"
            fullWidth
            status={buttonStatus}
            onClick={showCta ? onClick : undefined}
            // The DS Button wraps children in its own `button-label` span,
            // which is `inline-flex` and sizes to content. That swallows the
            // `w-full` on the wrapper below, re-opening the squeeze this
            // component fixed: the wrapper would size to the in-flow initial
            // label, and a longer `ctaLabel` ("Buy Failed" -> "Return to
            // Buy") is absolutely positioned to `inset-0`, so it would be
            // clipped by the `overflow-hidden` the slide needs. Stretching
            // the slot is the supported override — `button.intent.md`
            // documents `button-label` as an addressable part for exactly
            // this, and there is no prop for it.
            className="[&_[data-slot=button-label]]:w-full"
        >
            {/* `w-full` so the wrapper stretches to fill the Button's flex
                content area — without it, the wrapper sizes to whichever
                label happens to be visible (a flex item's auto width is
                content-driven), so a longer ctaLabel like "Return to Buy"
                gets squeezed when the initial label is shorter ("Buy
                Failed") and the wrapper is sized to the latter.

                `1.5em` height (was 1.25em) — 1.25em was tight enough to
                clip descenders (`g/p/y/q`) against the `overflow-hidden`
                edge needed for the slide transition. 1.5em gives the
                glyph box + descender room without changing the slide
                animation's character (`translateY(-100%)` scales with
                the wrapper). */}
            <span
                className="relative block overflow-hidden w-full"
                style={{ height: "1.5em" }}
            >
                {/* Fill the wrapper so this label centers like the overlaid CTA. */}
                <span
                    className="flex h-full items-center justify-center transition-all duration-500 ease-in-out"
                    style={initialLabelStyle}
                >
                    {successLabel}
                </span>
                <span
                    className="absolute inset-0 flex items-center justify-center transition-all duration-500 ease-in-out"
                    style={ctaLabelStyle}
                >
                    {ctaLabel}
                </span>
            </span>
        </Button>
    );
}
